import {
  FormMultiSelector,
  ComboboxItem,
  FormCombobox,
  FormSelect,
  Typography,
  FormInput,
  Separator,
  Label,
  Form,
} from '@ui/components';
import { useGetEmployeeProfiles } from '@shared/hooks/use-employee-profiles';
import { EmployeeTableDataDto, LabelType } from '@interfaces/models';
import { FormAssignTable } from '@shared/components/assign-table';
import { formatPhoneNumberToDisplay } from '@helpers/utils';
import { PhoneCodes, Masks } from '@helpers/constant';
import { FormLabelsPicker } from '@shared/components';
import { useGetCostCenters } from '@shared/hooks';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { defaultEditEmployeesValues, EditEmployeeForm } from './use-edit-employee-drawer-form';
import { useEmployeeAssignVehicles } from '../employee-assign-vehicles';

export const EmployeeDrawerForm = ({
  selectedEmployee,
  isEditBlocked,
  form,
}: {
  selectedEmployee: EmployeeTableDataDto | null;
  form: UseFormReturn<EditEmployeeForm>;
  isEditBlocked?: boolean;
}) => {
  const { t } = useTranslation();

  const { costCentersOptions, automaticAssignmentCostCenter } = useGetCostCenters();
  const { employeeProfilesOptions } = useGetEmployeeProfiles();

  const assignedVehicles = form.watch('assignedVehicles');
  const assignTable = useEmployeeAssignVehicles(assignedVehicles ?? []);

  const defaultProfile = employeeProfilesOptions.find((profile) => profile.isDefault);
  const selectedCostCenters = form.watch('costCenter');
  const selectedProfiles = form.watch('profiles');

  const profileOptions = employeeProfilesOptions
    .map((profile) => ({ ...profile, disabled: profile.isDefault }))
    .sort((a, b) => (a.isDefault ? 1 : b.isDefault ? -1 : 0));

  useEffect(() => {
    if (!selectedCostCenters && automaticAssignmentCostCenter) {
      form.setValue('costCenter', automaticAssignmentCostCenter.id);
    }
    if (!selectedProfiles.length && defaultProfile) {
      form.setValue('profiles', [defaultProfile.value]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    automaticAssignmentCostCenter,
    defaultProfile,
    selectedCostCenters,
    selectedProfiles,
    selectedEmployee,
  ]);

  useEffect(() => {
    const formValues = selectedEmployee
      ? {
          firstName: selectedEmployee.firstName ?? '',
          lastName: selectedEmployee.lastName ?? '',
          phoneNumber: formatPhoneNumberToDisplay(selectedEmployee.phoneNumber),
          phoneExt: selectedEmployee.phoneExt,
          email: selectedEmployee.email,
          labels: selectedEmployee.labels?.map((label) => label.id) ?? [],
          assignedVehicles: selectedEmployee.assignedVehicles?.map((vehicle) => vehicle.id) ?? [],
          profiles: selectedEmployee.profiles?.map((profile) => profile.id) ?? [],
          costCenter: selectedEmployee.costCenters?.[0]?.id ?? '',
          allowEmptyNames: !selectedEmployee.firstName || !selectedEmployee.lastName ? true : false,
        }
      : defaultEditEmployeesValues;

    form.reset(formValues);
  }, [selectedEmployee, form]);

  return (
    <Form {...form}>
      <form className="grid grid-cols-6 gap-2 items-center pt-1">
        <Label className="col-span-2" htmlFor="firstName">
          <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_FIRST_NAME')}</Typography>
        </Label>
        <FormInput
          placeholder={t('EMPLOYEES.DRAWER_FIRST_NAME')}
          className="w-full col-span-4"
          formControl={form.control}
          disabled={isEditBlocked}
          name="firstName"
          size="small"
        />

        <Label className="col-span-2" htmlFor="lastName">
          <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_LAST_NAME')}</Typography>
        </Label>
        <FormInput
          placeholder={t('EMPLOYEES.DRAWER_LAST_NAME')}
          className="w-full col-span-4"
          formControl={form.control}
          disabled={isEditBlocked}
          name="lastName"
          size="small"
        />

        <Label className="col-span-2" htmlFor="phoneNumber">
          <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_PHONE_NUMBER')}</Typography>
        </Label>
        <div className="grid grid-cols-4 gap-4 items-center col-span-4">
          <FormSelect
            placeholder={t('EMPLOYEES.DRAWER_PHONE_CODE')}
            className="w-full col-span-1"
            formControl={form.control}
            disabled={isEditBlocked}
            options={PhoneCodes}
            name="phoneExt"
            size="small"
          />
          <FormInput
            componentProps={{ maskOptions: { mask: Masks.PHONE } }}
            placeholder={t('EMPLOYEES.DRAWER_PHONE_NUMBER')}
            className="w-full col-span-3"
            formControl={form.control}
            disabled={isEditBlocked}
            name="phoneNumber"
            size="small"
          />
        </div>

        <Label className="col-span-2" htmlFor="email">
          <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_EMAIL')}</Typography>
        </Label>
        <FormInput
          placeholder={t('EMPLOYEES.DRAWER_EMAIL')}
          className="w-full col-span-4"
          formControl={form.control}
          disabled={isEditBlocked}
          name="email"
          size="small"
        />

        <Label className="col-span-2" htmlFor="labels">
          <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_LABELS')}</Typography>
        </Label>
        <FormLabelsPicker
          componentProps={{ hasShadow: true, id: 'labels' }}
          placeholder={t('EMPLOYEES.DRAWER_LABELS')}
          labelType={LabelType.EMPLOYEE}
          className="w-full col-span-4"
          formControl={form.control}
          disabled={isEditBlocked}
          name="labels"
          size="small"
        />

        <Label htmlFor="employeeProfile" className="col-span-2">
          <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_EMPLOYEE_PROFILE')}</Typography>
        </Label>

        <FormMultiSelector
          componentProps={{
            id: 'employeeProfile',
            contentSearch: {
              placeholder: t('EMPLOYEES.DRAWER_EMPLOYEE_PROFILE'),
            },
            badgeItems: true,
            badgeValue: true,
          }}
          placeholder={t('EMPLOYEES.DRAWER_EMPLOYEE_PROFILE')}
          className="w-full col-span-4"
          formControl={form.control}
          disabled={isEditBlocked}
          options={profileOptions}
          name="profiles"
          size="small"
        />

        {costCentersOptions?.length > 1 && (
          <>
            <Label className="col-span-2" htmlFor="costCenter">
              <Typography variant="labelSmallBook">{t('EMPLOYEES.DRAWER_COST_CENTER')}</Typography>
            </Label>
            <FormCombobox
              componentProps={{
                id: 'costCenter',
              }}
              placeholder={t('EMPLOYEES.DRAWER_COST_CENTER')}
              className="w-full col-span-4"
              options={costCentersOptions}
              formControl={form.control}
              disabled={isEditBlocked}
              name="costCenter"
              size="small"
            />
          </>
        )}

        <Separator className="col-span-6 my-2" />

        <div className="col-span-6">
          <FormAssignTable
            text={{
              removeButtonText: t('EMPLOYEES.VEHICLES_ASSIGN_REMOVE'),
              assignButtonText: t('EMPLOYEES.VEHICLES_ASSIGN_ADD'),
              comboboxPlaceholder: t('EMPLOYEES.VEHICLES_ASSIGN_COMBOBOX_PLACEHOLDER'),
              comboboxNoResults: t('EMPLOYEES.VEHICLES_ASSIGN_NO_RESULT'),
              tableNoResults: t('EMPLOYEES.VEHICLES_ASSIGN_NO_RESULT'),
              searchPlaceholder: t('EMPLOYEES.TABLE_SEARCH_PLACEHOLDER'),
              title: t('EMPLOYEES.VEHICLES_ASSIGN_TABLE_TITLE'),
              selectedRows: (count) =>
                t('EMPLOYEES.VEHICLES_ASSIGN_SELECTED_ROWS', {
                  postProcess: 'interval',
                  count,
                }),
            }}
            itemTemplate={(item) => (
              <ComboboxItem
                keywords={[item.name ?? '', item.registrationNumber ?? '']}
                disabled={assignedVehicles?.includes(item.id)}
                className="justify-between h-12"
                label={item.id}
                value={item.id}
                key={item.id}
              >
                <Typography variant="labelMediumBook">{item.name}</Typography>
                <Typography variant="labelMediumBook">{item.registrationNumber}</Typography>
              </ComboboxItem>
            )}
            formControl={form.control}
            disabled={isEditBlocked}
            name="assignedVehicles"
            {...assignTable}
          />
        </div>
      </form>
    </Form>
  );
};
